import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Sparkles, Check, ChevronDown, X as XIcon, CheckCircle, Star } from 'lucide-react';
import { motion } from 'framer-motion';
import HeroBackdrop from '../components/HeroBackdrop';
import ServiceIcon from '../components/ServiceIcon';
import Reveal from '../components/Reveal';
import AnimatedCounter from '../components/AnimatedCounter';
import PartnerBadges from '../components/PartnerBadges';
import ProcessSection from '../components/ProcessSection';
import FAQSection from '../components/FAQSection';
import useSEO from '../hooks/useSEO';
import useJsonLd from '../hooks/useJsonLd';
import { PAGE_SEO, SITE_CONFIG } from '../data/seoConfig';
import { HOME_FAQS } from '../data/faqs';
import { SERVICES, INDUSTRIES, MARKETS, PROCESS_STEPS, CASE_STUDIES, WHY_POINTS, HERO_STATS, TESTIMONIALS } from '../mock';

export default function Home() {
  useSEO(PAGE_SEO.home);

  // Service catalog schema — helps Google + AI assistants understand what services we offer
  useJsonLd('home-service-catalog', {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Digital Marketing Services',
    itemListElement: SERVICES.map((s, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'Service',
        name: s.title,
        description: s.short,
        url: `${SITE_CONFIG.url}/services/${s.slug}`,
        provider: { '@id': `${SITE_CONFIG.url}/#organization` },
      },
    })),
  });

  return (
    <div className='text-white overflow-x-hidden'>
      {/* HERO */}
      <section className='relative min-h-[100vh] flex items-center pt-28 pb-16 overflow-hidden'>
        <HeroBackdrop />
        <div className='relative max-w-6xl mx-auto px-6 w-full text-center'>
          <motion.div className='eyebrow-pill' initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.2, 0.7, 0.2, 1] }}>
            <Sparkles className='w-3.5 h-3.5' /> AI-Powered Digital Marketing Agency
          </motion.div>
          <motion.h1 className='mt-8 font-heading font-bold text-5xl md:text-7xl lg:text-8xl leading-[1.02] max-w-5xl mx-auto' initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1, ease: [0.2, 0.7, 0.2, 1] }}>
            <span className='text-brand-gradient'>The AI-Powered</span>
            <br />
            <span className='text-white'>Digital Marketing Agency</span>
          </motion.h1>
          <motion.p className='mt-8 text-lg md:text-2xl text-white/80 max-w-4xl mx-auto leading-relaxed font-body' initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.25, ease: [0.2, 0.7, 0.2, 1] }}>
            Ransen is a full-service digital marketing agency delivering SEO, paid media, LLM optimization, analytics, and conversion-focused growth for ambitious brands in 40+ cities worldwide.
          </motion.p>
          <motion.div className='mt-10 flex flex-col sm:flex-row items-center justify-center gap-4' initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.4, ease: [0.2, 0.7, 0.2, 1] }}>
            <Link to='/contact' className='btn-primary text-white font-semibold px-7 py-3.5 rounded-full inline-flex items-center gap-2 text-base'>
              Book a Strategy Call <ArrowRight className='w-4 h-4' />
            </Link>
            <Link to='/services' className='btn-secondary text-white font-semibold px-7 py-3.5 rounded-full inline-flex items-center gap-2 text-base'>
              Explore Services
            </Link>
          </motion.div>

          <motion.div className='mt-14 flex flex-col items-center text-white/50 text-xs font-accent' initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2, delay: 0.7 }}>
            <span className='tracking-[0.2em] uppercase'>Scroll to explore</span>
            <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}>
              <ChevronDown className='w-4 h-4 mt-1' />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className='py-20 bg-surface relative'>
        <GlowingOrbs />
        <div className='max-w-7xl mx-auto px-6 relative z-10'>
          <div className='grid grid-cols-2 lg:grid-cols-4 gap-8'>
            {HERO_STATS.map((s, i) => (
              <div key={s.label} className='text-center'>
                <div className='w-14 h-14 bg-brand/20 rounded-2xl flex items-center justify-center mx-auto mb-4'>
                  <ServiceIcon name={['Rocket','Globe2','LineChart','Users'][i] || 'Rocket'} className='w-6 h-6 text-brand' />
                </div>
                <div className='text-4xl md:text-5xl font-heading font-bold text-white mb-2'>
                  <AnimatedCounter value={s.value} />
                </div>
                <div className='text-white/60 font-body'>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className='relative py-14 border-y border-brand/10 bg-void'>
        <div className='max-w-6xl mx-auto px-6 text-center'>
          <p className='text-white/60 text-[13px] tracking-[0.2em] uppercase font-accent mb-8'>
            Certified partners & platforms we deploy
          </p>
          <PartnerBadges />
        </div>
      </section>

      {/* SERVICES */}
      <SectionHeader eyebrow='Our Global Services' title={<><span className='text-white'>Complete Marketing </span><span className='text-brand-gradient'>Solutions</span></>} desc='Ten interconnected services that share the same data model, forecasting layer, and reporting spine.' />
      <section className='pb-32 relative'>
        <GlowingOrbs />
        <div className='relative z-10 max-w-7xl mx-auto px-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6'>
          {SERVICES.map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`} className='glass-card rounded-2xl p-8 group'>
              <div className='flex items-start justify-between'>
                <div className='w-14 h-14 bg-gradient-to-br from-brand-dark to-brand rounded-2xl flex items-center justify-center shadow-lg shadow-brand/20 group-hover:scale-110 transition-transform'>
                  <ServiceIcon name={s.icon} className='w-6 h-6 text-white' />
                </div>
                <ArrowUpRight className='w-5 h-5 text-white/30 group-hover:text-brand group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all' />
              </div>
              <h3 className='mt-6 font-heading text-xl font-bold text-white group-hover:text-brand transition-colors'>{s.title}</h3>
              <p className='mt-3 text-[14.5px] text-white/60 leading-relaxed font-body'>{s.short}</p>
              <div className='mt-5 inline-flex items-center gap-1.5 text-brand text-[13.5px] font-medium'>
                Learn More <ArrowUpRight className='w-3.5 h-3.5' />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* WHY RANSEN - COMPARISON */}
      <section className='py-32 bg-surface relative overflow-hidden'>
        <GlowingOrbs />
        <div className='max-w-7xl mx-auto px-6 relative z-10'>
          <div className='text-center mb-16'>
            <div className='eyebrow-pill' style={{ background: 'rgba(0,240,255,0.15)', borderColor: 'rgba(0,240,255,0.35)', color: '#7DE7F5' }}>The Ransen Difference</div>
            <h2 className='mt-6 font-heading text-4xl md:text-6xl font-bold text-white leading-[1.05]'>
              Why AI-First Marketing<br />
              <span className='text-brand-gradient'>Changes Everything</span>
            </h2>
            <p className='mt-5 text-lg text-white/65 max-w-3xl mx-auto font-body'>
              We combine senior marketing strategy, technical tracking expertise, AI-powered optimization, and execution support to help businesses grow with clarity and confidence.
            </p>
          </div>

          <div className='grid md:grid-cols-2 gap-8 mb-16'>
            <div className='glass-card rounded-2xl p-8'>
              <div className='flex items-start mb-6'>
                <div className='w-14 h-14 bg-red-500/20 border border-red-500/30 rounded-2xl flex items-center justify-center mr-4'>
                  <XIcon className='text-red-400 w-7 h-7' />
                </div>
                <div>
                  <h3 className='font-heading text-xl font-bold text-white mb-1'>Traditional Agencies</h3>
                  <p className='text-white/50 text-sm'>The old way of doing things</p>
                </div>
              </div>
              <ul className='space-y-4'>
                {[
                  'Manual campaign management with delayed optimizations',
                  'Generic strategies applied to every client',
                  'Monthly reports that look backwards, not forwards',
                  'Limited to human working hours and capacity',
                  'No understanding of AI search (LLMO/AEO)',
                  'Reactive approach to market changes',
                ].map((item) => (
                  <li key={item} className='flex items-start text-white/70 text-[14.5px]'>
                    <div className='w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center mr-3 mt-0.5 shrink-0'>
                      <XIcon className='text-red-400 w-3 h-3' />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className='rounded-2xl p-8 bg-gradient-to-br from-brand/12 to-accent-cyan/5 backdrop-blur border border-brand/30 hover:border-brand/50 transition-colors'>
              <div className='flex items-start mb-6'>
                <div className='w-14 h-14 bg-gradient-to-br from-brand to-accent-cyan rounded-2xl flex items-center justify-center mr-4 shadow-lg shadow-brand/30'>
                  <Sparkles className='text-white w-7 h-7' />
                </div>
                <div>
                  <h3 className='font-heading text-xl font-bold text-white mb-1'>Ransen AI-First</h3>
                  <p className='text-brand text-sm'>The future of marketing</p>
                </div>
              </div>
              <ul className='space-y-4'>
                {[
                  'AI-powered real-time optimization 24/7/365',
                  'Custom strategies built on your unique data',
                  'Predictive analytics that anticipate trends',
                  'Unlimited scalability with AI automation',
                  'Pioneers in LLMO & AEO optimization',
                  'Proactive adaptation to algorithm changes',
                ].map((item) => (
                  <li key={item} className='flex items-start text-white/85 text-[14.5px]'>
                    <div className='w-5 h-5 rounded-full bg-brand flex items-center justify-center mr-3 mt-0.5 shrink-0'>
                      <CheckCircle className='text-white w-3 h-3' />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className='grid md:grid-cols-3 gap-6'>
            {WHY_POINTS.slice(0, 3).map((p, i) => (
              <div key={p.title} className='glass-card rounded-2xl p-6 text-center'>
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 ${i === 0 ? 'bg-brand/20' : i === 1 ? 'bg-accent-pink/20' : 'bg-accent-cyan/20'}`}>
                  <ServiceIcon name={p.icon} className={`w-6 h-6 ${i === 0 ? 'text-brand' : i === 1 ? 'text-accent-pink' : 'text-accent-cyan'}`} />
                </div>
                <h3 className='font-heading text-lg font-bold text-white mb-2'>{p.title}</h3>
                <p className='text-white/60 text-sm font-body'>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <SectionHeader eyebrow='Industries We Serve' title={<><span className='text-white'>Industry-Specific </span><span className='text-brand-gradient'>Marketing</span></>} desc='Specialized playbooks tuned to 12+ industries with category benchmarks and creative libraries.' />
      <section className='pb-32 relative'>
        <GlowingOrbs />
        <div className='relative z-10 max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'>
          {INDUSTRIES.map((ind) => (
            <Link key={ind.slug} to={`/industries/${ind.slug}`} className='glass-card rounded-2xl p-5 text-center group'>
              <div className='w-12 h-12 bg-brand/20 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-brand/30 transition-colors'>
                <ServiceIcon name={ind.icon} className='w-5 h-5 text-brand' />
              </div>
              <h3 className='font-heading font-bold text-white text-sm leading-tight'>{ind.title}</h3>
            </Link>
          ))}
        </div>
      </section>

      {/* LOCATIONS */}
      <section className='py-32 bg-surface relative overflow-hidden'>
        <GlowingOrbs />
        <div className='max-w-7xl mx-auto px-6 relative z-10 text-center'>
          <div className='eyebrow-pill'>Locations We Serve</div>
          <h2 className='mt-6 font-heading text-4xl md:text-6xl font-bold leading-[1.05]'>
            <span className='text-white'>Local Expertise. </span><span className='text-brand-gradient'>Everywhere You Grow.</span>
          </h2>
          <p className='mt-5 text-lg text-white/65 max-w-2xl mx-auto font-body'>
            City-level campaigns, country-level compliance, and localized creative across our top markets.
          </p>
          <div className='mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 max-w-5xl mx-auto'>
            {MARKETS.map((m) => (
              <Link key={m.slug} to={`/markets/${m.slug}`} className='glass-card rounded-2xl p-5 text-left group'>
                <div className='flex items-center gap-3'>
                  <div className='text-3xl'>{m.flag}</div>
                  <div>
                    <div className='font-heading font-bold text-white text-[15px]'>{m.name}</div>
                    <div className='text-brand text-[11px] tracking-wider uppercase mt-0.5 font-accent'>{m.region}</div>
                  </div>
                </div>
                <div className='mt-3 text-white/55 text-[12.5px]'>{m.tag}</div>
              </Link>
            ))}
          </div>
          <Link to='/locations' className='mt-10 inline-flex items-center gap-2 text-brand hover:text-white text-sm font-semibold transition-colors'>
            Explore all city hubs <ArrowRight className='w-4 h-4' />
          </Link>
        </div>
      </section>

      {/* PROCESS — creative scroll-triggered timeline */}
      <ProcessSection />

      {/* CASE STUDIES */}
      <section className='py-32 bg-surface relative'>
        <GlowingOrbs />
        <div className='max-w-7xl mx-auto px-6 relative z-10'>
          <div className='flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12'>
            <div>
              <div className='eyebrow-pill'>Case Studies</div>
              <h2 className='mt-6 font-heading text-4xl md:text-5xl font-bold leading-[1.05]'>
                <span className='text-white'>Outcomes We&rsquo;re </span><span className='text-brand-gradient'>Proud to Publish</span>
              </h2>
            </div>
            <Link to='/case-studies' className='btn-secondary text-white text-sm font-semibold px-5 py-2.5 rounded-full inline-flex items-center gap-2 self-start md:self-auto'>
              View all <ArrowRight className='w-4 h-4' />
            </Link>
          </div>
          <div className='grid md:grid-cols-2 gap-6'>
            {CASE_STUDIES.map((cs) => (
              <Link key={cs.slug} to='/case-studies' className='glass-card rounded-2xl overflow-hidden group'>
                <div className='relative aspect-[16/9] overflow-hidden'>
                  <img src={cs.image} alt={cs.title} className='w-full h-full object-cover transition-transform duration-700 group-hover:scale-105' />
                  <div className='absolute inset-0 bg-gradient-to-t from-void via-void/50 to-transparent' />
                  <div className='absolute top-4 left-4 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-white/85 text-[11px] tracking-wider uppercase font-accent'>{cs.industry}</div>
                </div>
                <div className='p-7'>
                  <h3 className='font-heading text-xl font-bold text-white'>{cs.title}</h3>
                  <div className='mt-2 text-brand text-sm font-medium'>{cs.metric}</div>
                  <p className='mt-3 text-[13.5px] text-white/60 leading-relaxed font-body'>{cs.summary}</p>
                  <div className='mt-5 flex flex-wrap gap-2'>
                    {cs.tags.map((t) => (
                      <span key={t} className='px-2.5 py-1 rounded-full bg-brand/10 border border-brand/25 text-[11px] text-white/70'>{t}</span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className='py-32 relative'>
        <GlowingOrbs />
        <div className='max-w-6xl mx-auto px-6 relative z-10'>
          <div className='text-center mb-14'>
            <div className='eyebrow-pill'>Client Success</div>
            <h2 className='mt-6 font-heading text-4xl md:text-6xl font-bold text-white leading-[1.05]'>What Our Clients Say</h2>
          </div>
          <div className='grid md:grid-cols-3 gap-6'>
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className='glass-card rounded-2xl p-7'>
                <div className='flex mb-4'>
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className='text-yellow-400 fill-yellow-400 w-4 h-4' />
                  ))}
                </div>
                <p className='text-white/85 text-[15px] leading-relaxed italic font-body'>&ldquo;{t.quote}&rdquo;</p>
                <div className='mt-6 pt-5 border-t border-brand/15'>
                  <div className='text-white font-heading font-bold text-[15px]'>{t.name}</div>
                  <div className='text-white/50 text-[12.5px]'>{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ — AEO/LLMO signal + featured snippet eligibility */}
      <FAQSection
        items={HOME_FAQS}
        schemaId='home-faq-schema'
        eyebrow='FAQ'
        title='Frequently Asked Questions'
        subtitle='Answers to the questions we hear most from ambitious brands considering a new digital marketing agency.'
      />

      {/* FINAL CTA */}
      <section className='relative py-24 bg-gradient-to-r from-brand-dark to-brand overflow-hidden'>
        <div className='absolute inset-0 opacity-20' style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        <div className='relative max-w-4xl mx-auto px-6 text-center'>
          <h2 className='font-heading text-4xl md:text-6xl font-bold text-white leading-[1.05]'>
            Ready to Scale Your Business?
          </h2>
          <p className='mt-6 text-lg md:text-xl text-white/90 max-w-2xl mx-auto font-body'>
            Join 150+ companies that have transformed their growth with Ransen.
          </p>
          <div className='mt-10 flex flex-col sm:flex-row items-center justify-center gap-4'>
            <Link to='/contact' className='bg-white text-brand-dark hover:bg-white/90 font-semibold px-8 py-4 rounded-full inline-flex items-center gap-2 shadow-xl transition-colors'>
              Book a Strategy Call <ArrowRight className='w-4 h-4' />
            </Link>
            <Link to='/case-studies' className='bg-white/10 hover:bg-white/20 text-white border border-white/25 font-semibold px-8 py-4 rounded-full inline-flex items-center gap-2 transition-colors'>
              See Case Studies
            </Link>
          </div>
          <div className='mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-white/85 text-[13px]'>
            {['Free 30-min diagnostic', 'No lock-in contracts', 'Global time zones covered'].map((f) => (
              <div key={f} className='inline-flex items-center gap-1.5'><Check className='w-3.5 h-3.5' /> {f}</div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function SectionHeader({ eyebrow, title, desc }) {
  return (
    <section className='pt-24 pb-14'>
      <div className='max-w-4xl mx-auto px-6 text-center'>
        <Reveal>
          <div className='eyebrow-pill'>{eyebrow}</div>
          <h2 className='mt-6 font-heading text-4xl md:text-6xl font-bold leading-[1.05]'>{title}</h2>
          {desc && <p className='mt-5 text-white/65 text-lg max-w-2xl mx-auto font-body'>{desc}</p>}
        </Reveal>
      </div>
    </section>
  );
}

function GlowingOrbs() {
  return (
    <div className='absolute inset-0 overflow-hidden pointer-events-none'>
      <div className='orb orb-brand w-96 h-96 top-1/4 left-1/4' />
      <div className='orb orb-pink w-80 h-80 bottom-1/4 right-1/4' />
      <div className='orb orb-cyan w-72 h-72 top-1/2 right-1/3' />
    </div>
  );
}
